import {
  buildFigmaFontIndex,
  type FigmaFontIndex,
  resolveFigmaFontForLayer,
} from "@hightouch/lib/ad-studio/html-editor/figma-font";
import { parseFontFamilyName } from "@hightouch/lib/ad-studio/html-editor/font-family";
import {
  cssWeightToNumber,
  deriveFontFamily,
} from "@hightouch/lib/brand-profiles/assets/font-identity";
import type { BrandFontFile } from "@hightouch/lib/brand-profiles/types";

/**
 * A style token spelling a bold-range weight. Bare `demi` counts — it is DemiBold's abbreviation
 * (`AvenirNextLTPro-Demi` is 600), so treating it as regular gets faux bold on top of the real cut.
 * The abbreviated cuts (`bd`, `blk`, `hv`) matter for the same reason: PostScript names cap at 31
 * characters, so a long family ships `LA28Sans-ExtBd` rather than `LA28Sans-ExtraBold`.
 */
const BOLD_FACE_TOKEN =
  /^(?:(?:semi|sem|demi|dem|extra|ext|ultra|ult|x)?(?:bold|bd|black|blk|heavy|hvy|hv)|demi)$/;
/**
 * A style token spelling a slant. Bare `it` counts (Adobe's abbreviation, `MinionPro-BoldIt`);
 * {@link styleTokens} splits camelCase, so `Outfit` and `Bit` stay one token and can't match.
 */
const ITALIC_FACE_TOKEN = /^(?:italic|oblique|it)$/;

export type PsdFontResolution = {
  /** PostScript-style name to write into the PSD's `FontSet`. */
  name: string;
  family?: string;
  style?: string;
  /** Face is already bold — don't ask Photoshop to synthesize on top. */
  isBoldFace: boolean;
  /** Face is already italic — same reasoning. */
  isItalicFace: boolean;
};

/** `null` when the family isn't a brand font — caller keeps its CSS family. */
export type PsdFontResolver = (args: {
  fontFamily: string;
  fontWeight: string;
  italic: boolean;
}) => PsdFontResolution | null;

export function buildPsdFontResolver(
  files: readonly BrandFontFile[],
): PsdFontResolver {
  const index = buildFigmaFontIndex(files);
  const facesByFamily = groupFacesByFamily(files);

  return ({ fontFamily, fontWeight, italic }) => {
    const normalized = parseFontFamilyName(fontFamily, "");
    // `resolveFigmaFontForLayer` always guesses an answer; an unknown family must keep the CSS name.
    if (!normalized || !index.has(normalized.toLowerCase())) return null;

    const { family, style } = resolveFigmaFontForLayer({
      index,
      fontFamily,
      fontWeight,
      italic,
    });
    const face = faceBehindLayer({
      index,
      facesByFamily,
      alias: normalized,
      family,
      fontWeight,
      italic,
    });
    const name = face?.postscriptName ?? derivedName(family, style);
    return {
      name,
      family,
      style,
      isBoldFace: denotesBoldFace(name),
      isItalicFace: denotesItalicFace(name),
    };
  };
}

/**
 * Fallback spelling when the binary has no PostScript name: `("Mulish", "Bold Italic")` →
 * `"Mulish-BoldItalic"`. A PSD stores one font name and gets no second attempt, unlike Figma, which
 * retries `deriveFigmaStyle`'s alternatives — so a family installing several cuts in one weight
 * bucket (Avenir's Heavy and Black) relies on the PostScript name above to tell them apart.
 */
function derivedName(family: string, style: string): string {
  const stem = family.replace(/\s+/g, "");
  const suffix = style.replace(/\s+/g, "");
  if (!stem) return suffix;
  return suffix ? `${stem}-${suffix}` : stem;
}

function groupFacesByFamily(
  files: readonly BrandFontFile[],
): Map<string, BrandFontFile[]> {
  const grouped = new Map<string, BrandFontFile[]>();
  for (const file of files) {
    const key = deriveFontFamily(file)?.toLowerCase();
    if (!key) continue;
    const existing = grouped.get(key);
    if (existing) existing.push(file);
    else grouped.set(key, [file]);
  }
  return grouped;
}

/**
 * The uploaded file the layer renders with, since `resolveFigmaFontForLayer` returns only
 * `(family, style)`. A filename alias indexes exactly one face; otherwise pick among the family's
 * cuts by slant and weight. Both paths stay inside the family it answered with.
 */
function faceBehindLayer(args: {
  index: FigmaFontIndex;
  facesByFamily: Map<string, BrandFontFile[]>;
  alias: string;
  family: string;
  fontWeight: string;
  italic: boolean;
}): BrandFontFile | undefined {
  const { index, facesByFamily, alias, family, fontWeight, italic } = args;
  const aliased = index.get(alias.toLowerCase());
  if (aliased?.length === 1) return aliased[0]?.file;
  const faces = facesByFamily.get(family.toLowerCase()) ?? [];
  // Slant narrows before weight: regular and italic sit at the same weight, so weight alone would
  // separate them by upload order. No cut of the requested slant → Photoshop synthesizes it.
  const slanted = faces.filter((file) => faceIsItalic(file) === italic);
  return nearestWeight(
    slanted.length > 0 ? slanted : faces,
    cssWeightToNumber(fontWeight),
  );
}

/** Whether the file is an italic cut: what the binary parsed, else how the face names itself. */
function faceIsItalic(file: BrandFontFile): boolean {
  const style = file.style?.trim().toLowerCase();
  if (style) return style === "italic" || style === "oblique";
  const stem = file.fileName?.replace(/\.[^.]+$/, "");
  for (const name of [file.preferredSubfamily, file.postscriptName, stem]) {
    if (name && denotesItalicFace(name)) return true;
  }
  return false;
}

/** Mirrors the resolver's own weight match, variable ranges (`"400 900"`) included. */
function nearestWeight(
  files: readonly BrandFontFile[],
  target: number,
): BrandFontFile | undefined {
  const distance = (file: BrandFontFile): number => {
    const parts = (file.weight ?? "").split(" ");
    const min = Number(parts[0]);
    if (!Number.isFinite(min)) return Number.MAX_SAFE_INTEGER;
    const max = parts[1] ? Number(parts[1]) : min;
    if (target >= min && target <= max) return 0;
    return Math.min(Math.abs(target - min), Math.abs(target - max));
  };
  return files.reduce<BrandFontFile | undefined>(
    (best, file) =>
      best === undefined || distance(file) < distance(best) ? file : best,
    undefined,
  );
}

/**
 * Whether a style (`"Semi Bold"`) or font name (`"BatonTurbo-Heavy"`) names an already-bold face.
 * Only a trailing token counts — a family named `Blackbird` has no bold cut.
 */
export function denotesBoldFace(styleOrName: string): boolean {
  const tokens = styleTokens(styleOrName);
  while (ITALIC_FACE_TOKEN.test(tokens.at(-1) ?? "")) tokens.pop();
  return BOLD_FACE_TOKEN.test(tokens.at(-1) ?? "");
}

/** Italic/oblique counterpart of {@link denotesBoldFace}. */
export function denotesItalicFace(styleOrName: string): boolean {
  const tokens = styleTokens(styleOrName);
  return ITALIC_FACE_TOKEN.test(tokens.at(-1) ?? "");
}

/**
 * Lowercased words, split on separators and camelCase so `SemiBold`/`semi-bold` tokenize alike
 * while `Blackbird` stays one word. Trailing digit tokens are dropped as build hashes.
 */
function styleTokens(value: string): string[] {
  const tokens = value
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z\d]+/)
    .filter(Boolean);
  while (/\d/.test(tokens.at(-1) ?? "")) tokens.pop();
  return tokens;
}
